import { useEffect, useMemo } from "react";

/**
 * Injects a JSON-LD <script> into <head> for the lifetime of the mounted
 * component. Uses a `data-jsonld-id` marker for identification so
 * React StrictMode double-mounts, HMR reloads, and route navigations
 * never leak orphan scripts.
 *
 * Usage:
 *   useJsonLd("faq-page", { "@context": "https://schema.org", ... });
 */
export function useJsonLd(id, data) {
  const json = useMemo(() => (data ? JSON.stringify(data) : null), [data]);

  useEffect(() => {
    if (!id || !json) return undefined;

    // Sweep any script we may have previously created under this id, this
    // is what prevents dev-mode / StrictMode / HMR from leaving orphans.
    document
      .querySelectorAll(`script[data-jsonld-id="${id}"]`)
      .forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-jsonld-id", id);
    script.textContent = json;
    document.head.appendChild(script);

    return () => {
      document
        .querySelectorAll(`script[data-jsonld-id="${id}"]`)
        .forEach((el) => {
          if (el.parentNode) el.parentNode.removeChild(el);
        });
    };
  }, [id, json]);
}
